import { useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { useControllableState } from '../lib/hooks';

export interface TabItem {
  value: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /**
   * `underline` (default) is the section tab — a rule under the strip, for
   * dividing a page into panels. `pill` wears the segmented control's rounded
   * track, for a small set of alternatives that read as one choice rather than
   * as navigation. Same semantics either way: this is the accessible tab
   * pattern, not a group of toggle buttons.
   */
  variant?: 'underline' | 'pill';
  'aria-label': string;
  className?: string;
}

/** Accessible tabs (roving arrow-key focus) — Staff editor schedule/overrides (§7.8). */
export function Tabs({ items, value, defaultValue, onChange, variant = 'underline', className, ...rest }: TabsProps) {
  const baseId = useId();
  const [active, setActive] = useControllableState<string>(value, defaultValue ?? items[0]!.value, onChange);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const focusTab = (index: number) => {
    const count = items.length;
    let next = index;
    for (let step = 0; step < count; step++) {
      next = (next + count) % count;
      if (!items[next]!.disabled) break;
    }
    const item = items[next]!;
    setActive(item.value);
    tabRefs.current[next]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusTab(index + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusTab(index - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusTab(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusTab(items.length - 1);
    }
  };

  const activeItem = items.find((i) => i.value === active) ?? items[0]!;

  const pill = variant === 'pill';

  return (
    <div className={cx('tc-tabs', pill && 'tc-tabs--pill', className)}>
      <div
        className={pill ? 'tc-segmented' : 'tc-tabs__list'}
        role="tablist"
        aria-label={rest['aria-label']}
      >
        {items.map((item, i) => {
          const selected = item.value === active;
          return (
            <button
              key={item.value}
              ref={(node) => {
                tabRefs.current[i] = node;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${item.value}`}
              aria-controls={`${baseId}-panel-${item.value}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              disabled={item.disabled}
              className={pill ? 'tc-segmented__item' : 'tc-tabs__tab'}
              onClick={() => setActive(item.value)}
              onKeyDown={(e) => onKeyDown(e, i)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`${baseId}-panel-${activeItem.value}`}
        aria-labelledby={`${baseId}-tab-${activeItem.value}`}
        className="tc-tabs__panel"
        tabIndex={0}
      >
        {activeItem.content}
      </div>
    </div>
  );
}
